import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PageHeader from "@/components/site/PageHeader";
import { useSEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Check,
    ArrowRight,
    Clock,
    ShieldCheck,
    Loader2,
    CheckCircle2,
    XCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass =
    "bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] placeholder:text-[#A1A1AA]/60 h-11";

export default function Paket() {
    useSEO({
        title: "Paket — fasta priser",
        description:
            "Köp testamente, äktenskapsförord, samboavtal, framtidsfullmakt, gåvobrev och bouppteckning direkt online — upprättat av jurist till fast pris.",
    });

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [statusOverlay, setStatusOverlay] = useState(null); // 'success' | 'cancelled' | null
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/shop/products`);
                setProducts(data.items || []);
            } catch {
                toast.error("Kunde inte hämta paketen.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Handle return from Stripe
    useEffect(() => {
        const payment = searchParams.get("payment");
        const sid = searchParams.get("session_id");
        if (payment === "success" && sid) {
            setStatusOverlay({ kind: "success", loading: true });
            (async () => {
                try {
                    const { data } = await axios.get(
                        `${API}/shop/order-status/${sid}`,
                    );
                    setStatusOverlay({ kind: "success", order: data });
                } catch {
                    setStatusOverlay({ kind: "success", order: null });
                }
            })();
        } else if (payment === "cancelled") {
            setStatusOverlay({ kind: "cancelled" });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearOverlay = () => {
        setStatusOverlay(null);
        searchParams.delete("payment");
        searchParams.delete("session_id");
        setSearchParams(searchParams, { replace: true });
    };

    const openCheckout = (p) => {
        setSelected(p);
        setForm({ name: "", email: "", phone: "", notes: "" });
    };

    const submit = async () => {
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Namn och e-post krävs.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/shop/checkout`, {
                product_id: selected.id,
                origin: window.location.origin,
                customer_name: form.name,
                customer_email: form.email,
                customer_phone: form.phone,
                notes: form.notes,
            });
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error("ingen checkout-url");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Kunde inte starta betalningen.",
            );
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-paket">
            <PageHeader
                kicker="Paket & fasta priser"
                title="Juridik online —"
                accent="upprättat av jurist."
                description="Välj det juridiska dokument du behöver, fyll i namn och e-post, betala säkert med kort eller Swish. En jurist kontaktar dig inom 24 timmar för ett kort intagssamtal — sedan levereras dokumentet till din inkorg."
            />

            <section className="py-20 bg-[#0A0A0B]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <p className="text-center text-[#A1A1AA]">Läser in...</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {products.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.6, delay: i * 0.05 }}
                                    data-testid={`paket-card-${p.id}`}
                                    className="flex flex-col bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 p-8 rounded-sm transition-colors duration-500"
                                >
                                    <p className="overline">{p.category}</p>
                                    <h3 className="mt-3 font-serif text-3xl text-[#F4F4F5]">
                                        {p.name}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                        {p.tagline}
                                    </p>
                                    <div className="my-7 flex items-end gap-2">
                                        <span className="font-serif text-5xl text-[#C4A977] leading-none">
                                            {p.price_sek.toLocaleString("sv-SE")}
                                        </span>
                                        <span className="text-[#A1A1AA] text-sm pb-2">
                                            kr · inkl. moms
                                        </span>
                                    </div>
                                    <ul className="space-y-2 text-sm text-[#F4F4F5]/90 flex-1">
                                        {p.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2">
                                                <Check size={14} className="text-[#C4A977] mt-1 shrink-0" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="mt-6 text-xs text-[#A1A1AA] inline-flex items-center gap-2">
                                        <Clock size={12} /> {p.delivery}
                                    </p>
                                    <Button
                                        onClick={() => openCheckout(p)}
                                        data-testid={`paket-buy-${p.id}`}
                                        className="mt-7 w-full bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-11 tracking-wide group"
                                    >
                                        Köp nu
                                        <ArrowRight
                                            size={14}
                                            className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                                        />
                                    </Button>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    <div className="mt-16 bg-[#111113] border border-white/5 p-8 rounded-sm grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="flex gap-3">
                            <ShieldCheck size={18} className="text-[#C4A977] mt-1 shrink-0" />
                            <div>
                                <p className="overline !text-[0.65rem]">Säker betalning</p>
                                <p className="mt-1 text-sm text-[#F4F4F5]/90">Kort eller Swish via Stripe — krypterat helt igenom.</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Check size={18} className="text-[#C4A977] mt-1 shrink-0" />
                            <div>
                                <p className="overline !text-[0.65rem]">Jurist på riktigt</p>
                                <p className="mt-1 text-sm text-[#F4F4F5]/90">Ingen mall — varje dokument upprättas individuellt utifrån dina önskemål.</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Clock size={18} className="text-[#C4A977] mt-1 shrink-0" />
                            <div>
                                <p className="overline !text-[0.65rem]">Snabb leverans</p>
                                <p className="mt-1 text-sm text-[#F4F4F5]/90">Vi kontaktar dig samma eller nästa arbetsdag efter beställning.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Checkout dialog */}
            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="bg-[#111113] border-white/10 text-[#F4F4F5] max-w-lg">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogDescription className="overline !text-[0.65rem]">
                                    {selected.category}  ·  {selected.price_sek.toLocaleString("sv-SE")} kr
                                </DialogDescription>
                                <DialogTitle className="font-serif text-2xl">
                                    Beställ {selected.name}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-2">
                                <div>
                                    <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">Ditt namn *</Label>
                                    <Input
                                        data-testid="paket-input-name"
                                        value={form.name}
                                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                        placeholder="För- och efternamn"
                                        className={`mt-2 ${inputClass}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">E-post *</Label>
                                    <Input
                                        data-testid="paket-input-email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                        placeholder="namn@exempel.se"
                                        className={`mt-2 ${inputClass}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">Telefon</Label>
                                    <Input
                                        data-testid="paket-input-phone"
                                        value={form.phone}
                                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                        placeholder="+46 70 000 00 00"
                                        className={`mt-2 ${inputClass}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">Kort info om ditt behov</Label>
                                    <Textarea
                                        data-testid="paket-input-notes"
                                        value={form.notes}
                                        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                        rows={3}
                                        placeholder="T.ex. vem som ska ärva, specialönskemål eller relevanta omständigheter."
                                        className={`mt-2 ${inputClass} !h-auto py-3`}
                                    />
                                </div>
                                <p className="text-xs text-[#A1A1AA]">
                                    Efter betalning kontaktar en jurist dig inom 24 timmar för ett kort intagssamtal innan dokumentet upprättas.
                                </p>
                            </div>
                            <DialogFooter className="mt-4">
                                <Button
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="paket-submit"
                                    className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-11 px-8"
                                >
                                    {submitting ? (
                                        <><Loader2 size={14} className="mr-2 animate-spin" /> Startar betalning...</>
                                    ) : (
                                        <>Betala {selected.price_sek.toLocaleString("sv-SE")} kr</>
                                    )}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Payment status overlay */}
            <Dialog open={!!statusOverlay} onOpenChange={(v) => !v && clearOverlay()}>
                <DialogContent
                    data-testid="paket-status-overlay"
                    className="bg-[#111113] border-white/10 text-[#F4F4F5] max-w-md text-center"
                >
                    {statusOverlay?.kind === "success" ? (
                        <div className="py-4">
                            <CheckCircle2 size={42} className="text-emerald-400 mx-auto" />
                            <h3 className="mt-5 font-serif text-3xl">Tack för din beställning!</h3>
                            <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                Vi har mottagit din betalning. En orderbekräftelse är
                                på väg till din inkorg, och en jurist kontaktar dig
                                inom 24 timmar.
                            </p>
                            {statusOverlay.order?.id && (
                                <p className="mt-4 text-xs text-[#A1A1AA]">
                                    Ordernummer: <span className="text-[#C4A977]">{statusOverlay.order.id}</span>
                                </p>
                            )}
                            <Button
                                onClick={clearOverlay}
                                className="mt-8 bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                            >
                                Stäng
                            </Button>
                        </div>
                    ) : (
                        <div className="py-4">
                            <XCircle size={42} className="text-amber-300 mx-auto" />
                            <h3 className="mt-5 font-serif text-3xl">Betalningen avbröts</h3>
                            <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                Ingen debitering har gjorts. Du kan försöka igen när som helst — eller kontakta oss om du har frågor.
                            </p>
                            <Button
                                onClick={clearOverlay}
                                className="mt-8 bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                            >
                                Stäng
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
